import Button from "@mui/material/Button";
import "./OverageEntry.css";
import AccordionDetails from "@mui/material/AccordionDetails";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import FormLabel from "@mui/material/FormLabel";
import FormGroup from "@mui/material/FormGroup";
import { useFormik } from "formik";
import { overageInvestigationNBSchema } from "../../schema";
import React, { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { APP, EFormAction, EOverageInvestigationStatus, ErrorMessages, M_GRID_PROPS, XL_GRID_PROPS } from "../../constants";
import Loader from "../../components/loader/loader";
import { PopoutNotification } from "../../AppState/popoutNotification";
import { SaveOrSubmitInvestigationAPI } from "../../API/saveOrSubmitInvestigation";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import isEqual from "lodash/isEqual";
import isEmpty from "lodash/isEmpty";
import { trimStrings } from "../../helpers";
import { isFormValueChanged } from "../../AppState/isFormValueChanged";
import { useImageUploader } from "../../@hooks/useImageUploader";
import { IOvEntry, IOvInvestigation } from "../../models/overage";
import OverageInvestigationTemplate from "../../templates/overageInvestigationTemplate";
import TooltipWithClickAway from "../../components/terminalPicker/TooltipWithClickAway";
import NoteBox from "../../components/noteBox/NoteBox";

// Question configuration
type QuestionConfig = {
  label: string;
  radioField: keyof IOvInvestigation;
  textField: keyof IOvInvestigation;
  tooltip?: string;
  tooltipKey?: string;
};

const NB_QUESTIONS: QuestionConfig[][] = [
  [
    {
      label: "Was OD400 created by the dock?",
      radioField: "od400Check",
      textField: "od400CheckResponse",
    },
    {
      label: "Did you utilize the markings off of the freight to find the PRO# that may apply?",
      radioField: "markingsCheck",
      textField: "markingsCheckResponse",
      tooltip: "Use option 5 FB master look up. Search: PO#, BOL#, SN #, MFG#, shipper/cons name etc.",
      tooltipKey: "tooltip-1",
    },
  ],
  [
    {
      label: "Did you view the trailer history?",
      radioField: "thCheck",
      textField: "thCheckResponse",
      tooltip: "Use option 62 to check other moves of this same trailer prior to your receipt. Check those pros and manifests.",
      tooltipKey: "tooltip-2",
    },
    {
      label: "If freight was found on a manifest, did you search that manifest for other shipments marked as short or undelivered?",
      radioField: "mfCheck",
      textField: "mfCheckResponse",
      tooltip: "Check OD400 for manifest #. Use option 16 for manifest history",
      tooltipKey: "tooltip-3",
    },
  ],
  [
    {
      label: "Did you check the OS&D master reporting for descriptions of open shortages and ADE's where the overage may apply?",
      radioField: "osAndDCheck",
      textField: "osAndDCheckResponse",
      tooltip: "Use menu option 78/18 to search for exceptions, example: AS/DS/NF/BNF/TS/ etc.",
      tooltipKey: "tooltip-4",
    },
    {
      label: "Did you google search the Manufacturer, distributor and/or product information? If so, did you use that information in freight bill tracing and/or OS&D searches?",
      radioField: "googleCheck",
      textField: "googleCheckResponse",
      tooltip: "Use options 5 & 78/18 to look for similar shipments with open shortages or ADE's.",
      tooltipKey: "tooltip-5",
    },
  ],
  [
    {
      label: " Did you check the unmanifested outbound and inbound freight reports to see if overage may match a non-movement shipment?",
      radioField: "unmanifestedCheck",
      textField: "unmanifestedCheckResponse",
      tooltip: "Use menu option 40 unmanifested outbound freight and option 41 unmanifested inbound freight to see if overage belongs to one of those shipments.",
      tooltipKey: "tooltip-6",
    },
    {
      label: "Did you search undelivered outbound and inbound shipments by shipper or account code that have not been delivered?",
      radioField: "undeliveredCheck",
      textField: "undeliveredCheckResponse",
      tooltip: "Use menu option 78/30.",
      tooltipKey: "tooltip-7",
    },
  ],
  [
    {
      label: "Did you use the customer delivery analysis report to check shipments picked up or going to a specific account code during a specific time frame?",
      radioField: "customerDeliveryCheck",
      textField: "customerDeliveryCheckResponse",
      tooltip: "Use AS400 menu options 85/20.",
      tooltipKey: "tooltip-8",
    },
    {
      label: "Did you check your undelivered and inventory reports for any shipments not yet delivered?",
      radioField: "undelInvCheck",
      textField: "undelInvCheckResponse",
      tooltip: "Use AS400 menu option 43 for undelivered and 87 for inventory.",
      tooltipKey: "tooltip-9",
    },
  ],
  [
    {
      label: "Did you send APB to ALLOSD (If the overage value is greater than $1,000)?",
      radioField: "apbCheck",
      textField: "apbCheckResponse",
    },
    {
      label: "Did you monitor all ALL OSD e-mail for possible match?",
      radioField: "emailCheck",
      textField: "emailCheckResponse",
    },
  ],
];

const OV_QUESTIONS: QuestionConfig[][] = [
  [
    {
      label: "Was OD400 created by the dock?",
      radioField: "od400Check",
      textField: "od400CheckResponse",
    },
    {
      label: "Do you check the bill of lading for a billing error?",
      radioField: "billOfLadingCheck",
      textField: "billOfLadingCheckResponse",
      tooltip: "Review piece count of what the driver signed for compared to what is shown on BOL.",
      tooltipKey: "tooltip-10",
    },
  ],
  [
    {
      label: "Did you check the DR to confirm what the consignee signed for?",
      radioField: "drCheck",
      textField: "drCheckResponse",
      tooltip: "Confirm the actual piece count signed for.",
      tooltipKey: "tooltip-11",
    },
    {
      label: "If a packing slip was found, did you check the packing slip to determine what the shipment consisted of?",
      radioField: "psCheck",
      textField: "psCheckResponse",
    },
  ],
  [
    {
      label: "Did you reach out to the consignee to verify what they received on their delivery?",
      radioField: "consigneeCheck",
      textField: "consigneeCheckResponse",
      tooltip: "Do not advise either customer that we have overage freight.",
      tooltipKey: "tooltip-12",
    },
    {
      label: "Did you check same day shipments at time of pickup to see if any are short and/or have ADE's that could have caused a misdelivery for your overage?",
      radioField: "shipmentCheck",
      textField: "shipmentCheckResponse",
      tooltip: "Example of exceptions to search for AS/DS/NF/BNF/TS/ etc.",
      tooltipKey: "tooltip-13",
    },
  ],
];

// Generate initial form values from question configurations
const generateInitialFormValues = (exceptionType?: string): Partial<IOvInvestigation> => {
  const initialValues: any = {
    osdType: exceptionType?.trim(),
  };
  
  const allQuestions = [...NB_QUESTIONS.flat(), ...OV_QUESTIONS.flat()];
  allQuestions.forEach((q) => {
    initialValues[q.radioField] = "";
    initialValues[q.textField] = "";
  });
  
  return initialValues as Partial<IOvInvestigation>;
};

type Props = {
  exceptionType?: string;
  osdNumber?: number;
  proNumber?: number | string | null;
  isEditMode?: boolean;
  investigationDetails?: IOvEntry ;
  setInvestigationStatus: (status: number) => void;
  expanded?: string[];
  isCleared?: boolean;
};
const InvestigationForm: React.FC<Props> = (props: Props): JSX.Element => {
  const dispatch = useDispatch();
  const { uploadImage } = useImageUploader();


  const showNotification = useCallback((type: NOTIFICATION_TYPE, message: string) => {
    dispatch(PopoutNotification(type, message, Date.now().toString()));
  }, [dispatch]);
 
  const initFormValue = React.useMemo<Partial<IOvInvestigation>>(
    () => props?.investigationDetails ?? generateInitialFormValues(props?.exceptionType),
    [props?.investigationDetails, props?.exceptionType]
  );
  const [formFieldsDisabled, setFormFieldsDisabled] = React.useState<boolean>(
    props?.isEditMode ?? false
  );
  const [isEditFormValuePatched, setIsEditFormValuePatched] =
    React.useState<boolean>(false);
  const [pageLoading, setPageLoading] = React.useState<boolean>(false);
  const [status, setStatus] = React.useState<number>(
    EOverageInvestigationStatus.exceptionEntry
  );
  const [isEditMode, setIsEditMode] = React.useState<boolean>(false);

  const enableEditing = useCallback((e: any) => {
    e.preventDefault();
    setFormFieldsDisabled(false);
  }, []);

  const investigationImageUpload = useCallback(async () => {
    await uploadImage({
      elementId: "req",
      osdNumber: props?.osdNumber ?? 0,
      setLoading: setPageLoading,
       headers: { "OSD-Module-Name": "overages", "OSD-Section-Name": "investigation", "OSD-Action-Type": "upload", "OSD-Exception-Type": props?.exceptionType ?? "", "OSD-Reporting-Terminal": props?.investigationDetails?.reporting ?? "", "OSD-Destination-Terminal": props?.investigationDetails?.destination ?? "" },
    });
  }, [props?.osdNumber, props?.exceptionType, uploadImage, props?.investigationDetails?.reporting, props?.investigationDetails?.destination]);

  const {
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    handleSubmit,
    setFieldValue,
    validateForm,
    resetForm,
  } = useFormik({
    validationSchema: overageInvestigationNBSchema,
    initialValues: initFormValue,
    validateOnChange: false,
    onSubmit: (values) => {
      saveOrSubmit("submit");
    },
  });

  const patchFormValue = useCallback(() => {
    resetForm();
    if (
      (props?.isEditMode || isEditMode) &&
      props?.investigationDetails
    ) {
      const source: IOvInvestigation = props.investigationDetails;
      const fields: Array<keyof IOvInvestigation> = [
        ...APP.OVInvestigationFields,
        ...APP.NBInvestigationFields,
      ];
      fields.forEach((f: keyof IOvInvestigation) => {
        const fieldValue = source[f];
        setFieldValue(
          f,
          typeof fieldValue === "string" ? fieldValue.trim() : ""
        );
      });
      setFieldValue("osdType", props?.exceptionType?.trim());
      setIsEditFormValuePatched(true);
    }
  }, [props?.isEditMode, props?.investigationDetails, props?.exceptionType, isEditMode, resetForm, setFieldValue]);

  const reset = useCallback((action: EFormAction) => {
    if (action === EFormAction.create) {
      resetForm();
      setFieldValue("osdType", props?.exceptionType?.trim());
      validateForm(values);
    } else if (action === EFormAction.edit) {
      patchFormValue();
    }
  }, [props?.exceptionType, resetForm, setFieldValue, validateForm, values, patchFormValue]);

  const saveOrSubmit = useCallback((action: string) => {
    setPageLoading(true);
    let reqParams = structuredClone(values) as Record<string, any>;

    const keysToKeep =
      reqParams.osdType === "NB"
        ? new Set(APP.NBInvestigationFields)
        : new Set(APP.OVInvestigationFields);

    Object.keys(reqParams).forEach((key) => {
      if (!keysToKeep.has(key)) {
        delete reqParams[key];
      }
    });

    reqParams["od400Check"] = values["od400Check"];
    reqParams["od400CheckResponse"] = values["od400CheckResponse"];
    reqParams = trimStrings(reqParams);
    SaveOrSubmitInvestigationAPI(reqParams, props.osdNumber!, action)
      .then((res) => {
        const message =
          action === "save"
            ? ErrorMessages.investigationNoteSaved
            : ErrorMessages.investigationNoteCompleted;
        showNotification(NOTIFICATION_TYPE.SUCCESS, message);

        if (action !== "save") {
          investigationImageUpload();
        }
        dispatch(isFormValueChanged(false));
        setIsEditFormValuePatched(false);
        setStatus(res.status);
        props?.setInvestigationStatus(res.status);
        setIsEditMode(true);
        setFormFieldsDisabled(true);
        validateForm(values);

        Object.keys(res).forEach((field: string) => {
          if (
            [
              ...APP.OVInvestigationFields,
              ...APP.NBInvestigationFields,
              "od400Check",
              "od400CheckResponse",
            ].includes(field)
          ) {
            const value = res[field];
            setFieldValue(field, value);
          }
        });
      })
      .catch((err) => {
        showNotification(
          NOTIFICATION_TYPE.ERROR,
          typeof err === "string"
            ? err
            : `OS&D investigation notes ${action} failed`
        );
      })
      .finally(() => {
        setPageLoading(false);
      });
  }, [values, props, showNotification, investigationImageUpload, dispatch, validateForm, setFieldValue]);

  const questionGroup = useCallback(
    (
      label: string,
      radioFieldName: keyof IOvInvestigation,
      textFieldName: keyof IOvInvestigation,
      toolTipText: string = "",
      toolTipKey: string = ""
    ) => {
      return (
        <Grid item {...M_GRID_PROPS}>
          <FormControl className="radio-group question-group ">
            <FormLabel id={radioFieldName}>
              {label} {toolTipText && <TooltipWithClickAway tooltipText={toolTipText} tooltipKey={toolTipKey} />}{" "}
            </FormLabel>
            <Grid container spacing={2}>
              <Grid item {...XL_GRID_PROPS} className="float-div textarea">
                <RadioGroup
                  row
                  aria-labelledby="od400Bill"
                  name={radioFieldName}
                  className="textarea-radio-group"
                  data-testid={radioFieldName}
                >
                  <FormControlLabel
                    value="Y"
                    control={<Radio />}
                    label="Yes"
                    name={radioFieldName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={formFieldsDisabled}
                    checked={values[radioFieldName] === "Y"}
                  />
                  <FormControlLabel
                    value="N"
                    control={<Radio />}
                    label="No"
                    name={radioFieldName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={formFieldsDisabled}
                    checked={values[radioFieldName] === "N"}
                  />
                </RadioGroup>
                <FormGroup className="form-row question-row">
                  <TextField
                    autoComplete="off"
                    multiline
                    rows={2}
                    value={values[textFieldName]}
                    id={textFieldName}
                    name={textFieldName}
                    key={textFieldName}
                    data-testid={textFieldName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={formFieldsDisabled}
                    placeholder="Describe Steps Taken"
                    inputProps={{ maxLength: 180 }}
                    variant="outlined"
                  />
                  {getErrorMessage(
                    errors[textFieldName] as string,
                    touched[textFieldName] as boolean
                  )}
                </FormGroup>
              </Grid>
            </Grid>
            {getErrorMessage(
              errors[radioFieldName] as string,
              touched[radioFieldName] as boolean
            )}
          </FormControl>
        </Grid>
      );
    },
    [values, errors, touched, handleChange, handleBlur, formFieldsDisabled]
  );

  const renderQuestionRows = useCallback((questions: QuestionConfig[][]) => {
    return questions.map((row) => (
      <Grid container spacing={2} className="filter-margin" key={row.map(q => q.radioField).join('-')}>
        {row.map((q) =>
          questionGroup(q.label, q.radioField, q.textField, q.tooltip, q.tooltipKey)
        )}
      </Grid>
    ));
  }, [questionGroup]);

  const getErrorMessage = (error: string, touched: boolean) => {
    if (!error || !touched) return null;
    return <span className="form-error hide-error">{error}</span>;
  };
  useEffect(() => {
    setFieldValue("osdType", props?.exceptionType?.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.exceptionType]);

  useEffect(() => {
    if (isEditFormValuePatched) {
      // Form values are already being managed by formik
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditFormValuePatched]);

  useEffect(() => {
    patchFormValue();
    setStatus(
      props?.investigationDetails?.investigationStatus ??
        EOverageInvestigationStatus.exceptionEntry
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.investigationDetails]);

  useEffect(() => {
    if (props?.isEditMode && props?.investigationDetails) {
      validateForm(values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  useEffect(() => {
    if (!props?.expanded?.includes("panel2")) {
      reset(
        props?.isEditMode || isEditMode ? EFormAction.edit : EFormAction.create
      );
      setFormFieldsDisabled(props?.isEditMode ?? isEditMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.expanded]);

  useEffect(() => {
    if (isEditFormValuePatched || !props?.isEditMode) {
      dispatch(isFormValueChanged(!isEqual(initFormValue, values)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditFormValuePatched, values, initFormValue]);

  return (
    <div>
      <form action="" onSubmit={handleSubmit}>
        <AccordionDetails>
          <div>
            <div className="print-doc">
              <br />
              <span className="print-title">
                Overage Investigation - Exception{" "}
              </span>
              <span className="print-title">#{props?.osdNumber}</span>
              {props?.exceptionType === "OV" && (
                <>
                  , <span className="print-title">&nbsp; Pro #</span>
                  <span className="print-title">{props?.proNumber}</span>
                </>
              )}
              <br />
            </div>

            {props?.exceptionType === "NB" && renderQuestionRows(NB_QUESTIONS)}

            {props?.exceptionType === "OV" && renderQuestionRows(OV_QUESTIONS)}
          </div>
          <div>
            <div className="hide-me" id="req">
              <OverageInvestigationTemplate
                investigationValues={values}
                osdNumber={props.osdNumber}
                exceptionType={props.exceptionType}
              />
            </div>
          </div>

          {status !== EOverageInvestigationStatus.complete &&
            !isEmpty(errors) && (
              <div className="font-weight">
                Note:{" "}
                <i>
                  To complete & image your investigation, please answer all
                  investigation questions (Radio buttons and Text boxes).
                </i>
              </div>
            )}
          {!props.isCleared && 
            <div className="btn-group-submit">
              {formFieldsDisabled &&
                status !== EOverageInvestigationStatus.complete && (
                  <>
                    <Button
                      onClick={enableEditing}
                      variant="contained"
                      className="assign mr-0 ml"
                      type="button"
                      data-testid="ov-investigation-edit-button"
                    >
                      Edit
                    </Button>
                    {isEmpty(errors) && (
                      <Button
                        variant="contained"
                        className="entry ml"
                        type="submit"
                        data-testid="ov-investigation-complete-button"
                      >
                        Complete & Image
                      </Button>
                    )}
                  </>
                )}
              {!formFieldsDisabled && (
                <>
                  <Button
                    data-testid="ov-investigation-cancel-button"
                    onClick={() =>
                      reset(
                        props?.isEditMode || isEditMode
                          ? EFormAction.edit
                          : EFormAction.create
                      )
                    }
                    variant="contained"
                    className="clear-btn mr-0 ml"
                    type="button"
                  >
                    {props?.isEditMode || isEditMode ? "Cancel" : "Clear"}
                  </Button>
                  <Button
                    type="button"
                    data-testid="ov-investigation-save-button"
                    onClick={(e) => {
                      saveOrSubmit("save");
                    }}
                    variant="contained"
                    className="entry ml"
                  >
                    Save
                  </Button>
                </>
              )}
            </div>
          }
        </AccordionDetails>
      </form>
      <Loader pageLoader={pageLoading}></Loader>
     
       <NoteBox message="Note: If you located a shipment belonging to a pro that has already delivered clear, send an e-mail to the OS&D clerk at the terminal to verify order before sending freight to the DT."/>
    </div>
  );
};

export default InvestigationForm;
